import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import warehouseSlipApi, {
  warehouseSlipOperatorApi,
} from "../../../../../infrastructure/api/warehouseSlipApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const pick = (row, camel, pascal) => row?.[camel] ?? row?.[pascal];

const SLIP_TYPE = { Inbound: 1, Outbound: 2 };
const STATUS = { Draft: 0, Sent: 1, Reviewed: 2, Completed: 3, Cancelled: 9 };

const STATUS_OPTIONS = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: "0", label: "پیش‌نویس" },
  { value: "1", label: "ارسال شد" },
  { value: "2", label: "بررسی شد" },
  { value: "3", label: "تأیید نهایی" },
  { value: "9", label: "لغو شده" },
];

const TYPE_OPTIONS = [
  { value: "", label: "همه انواع" },
  { value: "1", label: "ورود" },
  { value: "2", label: "خروج" },
];

const statusColor = (status) => {
  switch (Number(status)) {
    case 0:
      return "default";
    case 1:
      return "info";
    case 2:
      return "warning";
    case 3:
      return "success";
    case 9:
      return "error";
    default:
      return "default";
  }
};

const openPdfBlob = async (blob, fileName = "warehouse-slip.pdf") => {
  if (blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    let msg = "خطا در تولید PDF";
    try {
      const json = JSON.parse(text);
      msg = json.message || json.Message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const url = window.URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

const openHtmlPrint = (html) => {
  const w = window.open("", "_blank", "noopener,noreferrer");
  if (!w) {
    toast.error("پنجره پرینت مسدود شد؛ پاپ‌آپ را اجازه دهید");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {
      /* ignore */
    }
  }, 400);
};

const emptyLine = () => ({
  skuCode: "",
  itemTitle: "",
  unitTitle: "عدد",
  quantity: "",
  notes: "",
});

const emptyForm = () => ({
  slipType: SLIP_TYPE.Inbound,
  warehouseId: "",
  counterpartyName: "",
  referenceNo: "",
  warehouseManagerName: "",
  notes: "",
  lines: [emptyLine()],
});

export default function WarehouseSlipsPage({ source = "Management" }) {
  const api =
    source === "SolarentOperator" ? warehouseSlipOperatorApi : warehouseSlipApi;

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [slipType, setSlipType] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [stockOpen, setStockOpen] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPagedList({
        search: search || undefined,
        status: status === "" ? undefined : Number(status),
        slipType: slipType === "" ? undefined : Number(slipType),
        page: 1,
        pageSize: 100,
      });
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "خطا در دریافت لیست");
        return;
      }
      const data = res?.data ?? res?.Data ?? {};
      setItems(pick(data, "items", "Items") || []);
      setStats({
        draft: pick(data, "draftCount", "DraftCount") || 0,
        sent: pick(data, "sentCount", "SentCount") || 0,
        reviewed: pick(data, "reviewedCount", "ReviewedCount") || 0,
        completed: pick(data, "completedCount", "CompletedCount") || 0,
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت لیست");
    } finally {
      setLoading(false);
    }
  }, [api, search, status, slipType]);

  const loadWarehouses = useCallback(async () => {
    try {
      const res = await api.getWarehouses();
      const data = res?.data ?? res?.Data ?? [];
      setWarehouses(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  }, [api]);

  useEffect(() => {
    loadList();
    loadWarehouses();
  }, [loadList, loadWarehouses]);

  const openCreate = (type) => {
    const defWh = warehouses.find((w) => pick(w, "isDefault", "IsDefault"));
    setForm({
      ...emptyForm(),
      slipType: type,
      warehouseId: pick(defWh, "id", "Id") || "",
    });
    setFormOpen(true);
  };

  const saveCreate = async () => {
    const lines = (form.lines || [])
      .filter((l) => String(l.itemTitle || "").trim())
      .map((l) => ({
        skuCode: l.skuCode || null,
        itemTitle: String(l.itemTitle).trim(),
        unitTitle: l.unitTitle || "عدد",
        quantity: Number(l.quantity),
        notes: l.notes || null,
      }));
    if (!lines.length || lines.some((l) => !l.quantity || l.quantity <= 0)) {
      toast.error("اقلام و مقدار را کامل وارد کنید");
      return;
    }
    try {
      const res = await api.create({
        slipType: Number(form.slipType),
        warehouseId: form.warehouseId ? Number(form.warehouseId) : null,
        counterpartyName: form.counterpartyName || null,
        referenceNo: form.referenceNo || null,
        warehouseManagerName: form.warehouseManagerName || null,
        notes: form.notes || null,
        lines,
      });
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "ثبت ناموفق");
        return;
      }
      toast.success(res?.message || res?.Message || "ثبت شد");
      setFormOpen(false);
      loadList();
    } catch (e) {
      toast.error(e?.message || "ثبت ناموفق");
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.getDetail(id);
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "یافت نشد");
        return;
      }
      setDetail(res?.data ?? res?.Data);
    } catch (e) {
      toast.error(e?.message || "خطا");
    }
  };

  const runAction = async (action, id) => {
    try {
      const res = await api[action]({ id });
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "عملیات ناموفق");
        return;
      }
      toast.success(res?.message || res?.Message || "انجام شد");
      const data = res?.data ?? res?.Data;
      setDetail(data);
      loadList();
    } catch (e) {
      toast.error(e?.message || "عملیات ناموفق");
    }
  };

  const printPdf = async (id) => {
    const readAxiosMessage = async (err) => {
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          return json.message || json.Message || err.message;
        } catch {
          return err?.message;
        }
      }
      return data?.message || data?.Message || err?.message;
    };

    try {
      const res = await api.downloadPdf(id);
      await openPdfBlob(res.data, `warehouse-slip-${id}.pdf`);
    } catch (e) {
      try {
        const htmlRes = await api.downloadHtml(id);
        const html = typeof htmlRes.data === "string" ? htmlRes.data : String(htmlRes.data || "");
        if (!html || html.length < 20) throw e;
        openHtmlPrint(html);
        toast.info("PDF در دسترس نبود؛ پرینت مرورگر باز شد");
      } catch (e2) {
        const msg = (await readAxiosMessage(e)) || (await readAxiosMessage(e2)) || "خطا در تولید PDF";
        toast.error(msg);
      }
    }
  };

  const loadStock = async () => {
    try {
      const res = await api.getStockBalances({});
      const data = res?.data ?? res?.Data ?? [];
      setStock(Array.isArray(data) ? data : []);
      setStockOpen(true);
    } catch (e) {
      toast.error(e?.message || "خطا در موجودی");
    }
  };

  const detailStatus = detail ? Number(pick(detail, "status", "Status")) : null;
  const detailType = detail ? Number(pick(detail, "slipType", "SlipType")) : null;

  const actionButtons = useMemo(() => {
    if (detailStatus == null) return null;
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {detailStatus === STATUS.Draft && (
          <Button variant="contained" onClick={() => runAction("markSent", pick(detail, "id", "Id"))}>
            ارسال به انبار
          </Button>
        )}
        {detailStatus === STATUS.Sent && (
          <Button variant="contained" color="warning" onClick={() => runAction("markReviewed", pick(detail, "id", "Id"))}>
            تأیید بررسی
          </Button>
        )}
        {detailStatus === STATUS.Reviewed && (
          <Button variant="contained" color="success" onClick={() => runAction("confirmComplete", pick(detail, "id", "Id"))}>
            {detailType === SLIP_TYPE.Outbound ? "تأیید خروج" : "تأیید ورود به انبار"}
          </Button>
        )}
        {detailStatus !== STATUS.Completed && detailStatus !== STATUS.Cancelled && (
          <Button color="error" onClick={() => runAction("cancel", pick(detail, "id", "Id"))}>
            لغو
          </Button>
        )}
        <Button startIcon={<PrintIcon />} onClick={() => printPdf(pick(detail, "id", "Id"))}>
          پرینت / PDF
        </Button>
      </Stack>
    );
  }, [detail, detailStatus, detailType]);

  return (
    <Box>
      <BreadcrumbsComponent />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>
          برگه‌های انبار
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<WarehouseIcon />} onClick={loadStock}>
            موجودی انبار
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openCreate(SLIP_TYPE.Inbound)}>
            ورود انبار
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate(SLIP_TYPE.Outbound)}>
            خروج انبار
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        چرخه: پیش‌نویس → ارسال شد (مهر) → بررسی شد (مهر) → تأیید نهایی (مهر خروج/ورود) — سپس PDF با ریز اقلام برای امضای مسئول انبار.
      </Alert>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <Chip label={`پیش‌نویس: ${stats.draft || 0}`} />
        <Chip color="info" label={`ارسال‌شده: ${stats.sent || 0}`} />
        <Chip color="warning" label={`بررسی‌شده: ${stats.reviewed || 0}`} />
        <Chip color="success" label={`تأیید نهایی: ${stats.completed || 0}`} />
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="جستجو (شماره / طرف / مرجع)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>وضعیت</InputLabel>
              <Select label="وضعیت" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "all"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>نوع</InputLabel>
              <Select label="نوع" value={slipType} onChange={(e) => setSlipType(e.target.value)}>
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "all-type"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={loadList} disabled={loading}>
              بروزرسانی
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>شماره</TableCell>
              <TableCell>نوع</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>انبار</TableCell>
              <TableCell>طرف مقابل</TableCell>
              <TableCell>اقلام</TableCell>
              <TableCell>مهرها</TableCell>
              <TableCell align="left">عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => {
              const id = pick(row, "id", "Id");
              return (
                <TableRow key={id} hover>
                  <TableCell>{pick(row, "slipNumber", "SlipNumber")}</TableCell>
                  <TableCell>{pick(row, "slipTypeTitle", "SlipTypeTitle")}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(pick(row, "status", "Status"))}
                      label={pick(row, "statusTitle", "StatusTitle")}
                    />
                  </TableCell>
                  <TableCell>{pick(row, "warehouseTitle", "WarehouseTitle")}</TableCell>
                  <TableCell>{pick(row, "counterpartyName", "CounterpartyName") || "—"}</TableCell>
                  <TableCell>{pick(row, "lineCount", "LineCount")}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {pick(row, "hasSentStamp", "HasSentStamp") && <Chip size="small" color="info" label="ارسال شد" />}
                      {pick(row, "hasReviewedStamp", "HasReviewedStamp") && (
                        <Chip size="small" color="success" label="بررسی شد" />
                      )}
                      {pick(row, "hasExitStamp", "HasExitStamp") && <Chip size="small" color="error" label="خروج" />}
                    </Stack>
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => openDetail(id)}>
                        جزئیات
                      </Button>
                      <IconButton size="small" onClick={() => printPdf(id)} title="PDF">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {!items.length && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  موردی نیست
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {Number(form.slipType) === SLIP_TYPE.Outbound ? "ثبت خروج انبار" : "ثبت ورود انبار"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>انبار</InputLabel>
                <Select
                  label="انبار"
                  value={form.warehouseId}
                  onChange={(e) => setForm((f) => ({ ...f, warehouseId: e.target.value }))}
                >
                  {warehouses.map((w) => (
                    <MenuItem key={pick(w, "id", "Id")} value={pick(w, "id", "Id")}>
                      {pick(w, "title", "Title")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="طرف مقابل"
                value={form.counterpartyName}
                onChange={(e) => setForm((f) => ({ ...f, counterpartyName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="شماره مرجع"
                value={form.referenceNo}
                onChange={(e) => setForm((f) => ({ ...f, referenceNo: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="مسئول انبار (برای چاپ)"
                value={form.warehouseManagerName}
                onChange={(e) => setForm((f) => ({ ...f, warehouseManagerName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="یادداشت"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            ریز اقلام
          </Typography>
          {(form.lines || []).map((line, idx) => (
            <Grid container spacing={1} key={idx} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="کد"
                  value={line.skuCode}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], skuCode: e.target.value };
                    setForm((f) => ({ ...f, lines }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="شرح کالا"
                  value={line.itemTitle}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], itemTitle: e.target.value };
                    setForm((f) => ({ ...f, lines }));
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="واحد"
                  value={line.unitTitle}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], unitTitle: e.target.value };
                    setForm((f) => ({ ...f, lines }));
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="مقدار"
                  type="number"
                  value={line.quantity}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], quantity: e.target.value };
                    setForm((f) => ({ ...f, lines }));
                  }}
                />
              </Grid>
              <Grid item xs={10} md={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="شرح"
                  value={line.notes}
                  onChange={(e) => {
                    const lines = [...form.lines];
                    lines[idx] = { ...lines[idx], notes: e.target.value };
                    setForm((f) => ({ ...f, lines }));
                  }}
                />
              </Grid>
              <Grid item xs={2} md={1}>
                <IconButton
                  disabled={form.lines.length <= 1}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      lines: f.lines.filter((_, i) => i !== idx),
                    }))
                  }
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button size="small" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))}>
            + قلم جدید
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>انصراف</Button>
          <Button variant="contained" onClick={saveCreate}>
            ثبت پیش‌نویس
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {pick(detail, "slipNumber", "SlipNumber")} — {pick(detail, "slipTypeTitle", "SlipTypeTitle")}
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                <Chip
                  color={statusColor(pick(detail, "status", "Status"))}
                  label={pick(detail, "statusTitle", "StatusTitle")}
                />
                {pick(detail, "hasSentStamp", "HasSentStamp") && <Chip color="info" label="مهر: ارسال شد" />}
                {pick(detail, "hasReviewedStamp", "HasReviewedStamp") && (
                  <Chip color="success" label="مهر: بررسی شد" />
                )}
                {pick(detail, "hasExitStamp", "HasExitStamp") && <Chip color="error" label="مهر: خروج" />}
              </Stack>
              <Typography variant="body2" mb={1}>
                انبار: {pick(detail, "warehouseTitle", "WarehouseTitle")} | طرف:{" "}
                {pick(detail, "counterpartyName", "CounterpartyName") || "—"}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ردیف</TableCell>
                    <TableCell>کد</TableCell>
                    <TableCell>شرح</TableCell>
                    <TableCell>واحد</TableCell>
                    <TableCell>مقدار</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pick(detail, "lines", "Lines") || []).map((line) => (
                    <TableRow key={pick(line, "id", "Id")}>
                      <TableCell>{pick(line, "lineNumber", "LineNumber")}</TableCell>
                      <TableCell>{pick(line, "skuCode", "SkuCode") || "—"}</TableCell>
                      <TableCell>{pick(line, "itemTitle", "ItemTitle")}</TableCell>
                      <TableCell>{pick(line, "unitTitle", "UnitTitle")}</TableCell>
                      <TableCell>{pick(line, "quantity", "Quantity")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box mt={2}>{actionButtons}</Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>بستن</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stockOpen} onClose={() => setStockOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>موجودی انبار</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>انبار</TableCell>
                <TableCell>کد</TableCell>
                <TableCell>کالا</TableCell>
                <TableCell>موجودی</TableCell>
                <TableCell>قابل استفاده</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stock.map((row) => (
                <TableRow key={pick(row, "id", "Id")}>
                  <TableCell>{pick(row, "warehouseTitle", "WarehouseTitle")}</TableCell>
                  <TableCell>{pick(row, "skuCode", "SkuCode")}</TableCell>
                  <TableCell>{pick(row, "itemTitle", "ItemTitle")}</TableCell>
                  <TableCell>{pick(row, "quantityOnHand", "QuantityOnHand")}</TableCell>
                  <TableCell>{pick(row, "quantityAvailable", "QuantityAvailable")}</TableCell>
                </TableRow>
              ))}
              {!stock.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    موجودی ثبت نشده
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockOpen(false)}>بستن</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
